import { CATALOG_ONBOARDING } from "./mockData";
import { StatusPill, SectionHeader, Stat } from "./ui";
import { FileSpreadsheet, FileText, AlertTriangle, CheckCircle2, XCircle, Upload, Loader2, AlertOctagon } from "lucide-react";
import { useFetch } from "./api/useFetch";
import { adaptCatalogImport, pickLatestCatalogImport, unwrap } from "./api/adapters";
import { isSurfaceLive } from "./api/platformClient";
import { LiveBadge, SourceTag } from "./ui-badges";

export const CatalogOnboarding = ({ clientId, clientName }) => {
  const live = isSurfaceLive("CATALOG_IMPORTS");
  const { data: liveData, error: liveError, loading: liveLoading } = useFetch(
    live && clientId ? `/api/platform/companies/${clientId}/catalog-imports` : null
  );

  // Adapter logic: accept envelope { ok, runs:[...] } OR array OR single import object.
  let liveState = null;
  if (live && liveData) {
    let runs = unwrap(liveData, "runs");
    if (!Array.isArray(runs)) runs = Array.isArray(liveData) ? liveData : null;
    const latest = Array.isArray(runs) ? pickLatestCatalogImport(runs) : (runs || liveData);
    liveState = latest ? adaptCatalogImport(latest) : null;
  }

  const mockState = CATALOG_ONBOARDING[clientId];
  const state = live && liveState ? liveState : mockState;

  if (live && liveLoading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-10 text-center" data-testid="catalog-loading">
        <Loader2 className="mx-auto h-6 w-6 text-slate-400 animate-spin" />
        <p className="mt-3 text-sm text-slate-500">Loading catalog imports…</p>
      </div>
    );
  }

  if (live && liveError) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-5" data-testid="catalog-error">
        <div className="flex items-center gap-3 mb-2">
          <AlertOctagon className="h-5 w-5 text-rose-700" />
          <div className="text-sm font-semibold text-rose-900">
            Catalog imports request failed
          </div>
          <div className="ml-auto"><LiveBadge live={live} /></div>
        </div>
        <p className="text-xs text-rose-800">{liveError.message}</p>
      </div>
    );
  }

  if (!state || !state.file) {
    return (
      <div
        data-testid="catalog-onboarding-empty"
        className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center"
      >
        <div className="flex justify-end mb-2"><LiveBadge live={live} /></div>
        <Upload className="mx-auto h-8 w-8 text-slate-400" />
        <h3 className="mt-3 text-sm font-semibold text-slate-900">
          No catalog uploaded yet
        </h3>
        <p className="mt-1 text-sm text-slate-500">
          {clientName ? `${clientName} has not started catalog onboarding.` : "Drop a CSV or Excel file to begin."}
        </p>
        <div className="mt-5 flex items-center justify-center gap-3">
          <MockUploadButton icon={<FileText className="h-4 w-4" />} label="Upload CSV" testId="upload-csv-btn" />
          <MockUploadButton icon={<FileSpreadsheet className="h-4 w-4" />} label="Upload Excel" testId="upload-excel-btn" />
        </div>
        <p className="mt-4 text-[11px] text-slate-400">
          {live ? "Read-only view — uploads are not enabled in Phase 2C." : "Mock UI — no file processing occurs in this preview."}
        </p>
      </div>
    );
  }

  const approvalPct = Math.round((state.approvedRows / state.rowsTotal) * 100);

  return (
    <div data-testid={`catalog-onboarding-${clientId}`} className="space-y-4">
      <SectionHeader
        title="Catalog onboarding center"
        subtitle={clientName ? `Importing for ${clientName}` : undefined}
        right={
          <div className="flex items-center gap-3">
            <LiveBadge live={live} />
            {!live && (
              <div className="flex gap-2">
                <MockUploadButton icon={<FileText className="h-4 w-4" />} label="Upload CSV" testId="upload-csv-btn" />
                <MockUploadButton icon={<FileSpreadsheet className="h-4 w-4" />} label="Upload Excel" testId="upload-excel-btn" />
              </div>
            )}
          </div>
        }
      />

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-start gap-4">
          <div className="rounded-lg bg-slate-100 p-3">
            {state.file.type === "CSV" ? (
              <FileText className="h-6 w-6 text-slate-600" />
            ) : (
              <FileSpreadsheet className="h-6 w-6 text-slate-600" />
            )}
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-900">{state.file.name}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {state.file.type} • {state.file.size} • Uploaded {state.file.uploadedAt}
            </div>
          </div>
          <StatusPill status={state.mapping.status} />
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-slate-500 mb-1.5">
            <span>Column mapping</span>
            <span className="font-medium text-slate-700">
              {state.mapping.mappedColumns} / {state.mapping.totalColumns} columns mapped
            </span>
          </div>
          <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-[#0d3b66] transition-all"
              style={{ width: `${state.mapping.percent}%` }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Total rows" value={state.rowsTotal.toLocaleString()} testId="catalog-stat-total" />
        <Stat
          label="Approved rows"
          value={state.approvedRows.toLocaleString()}
          sub={`${approvalPct}% of total`}
          testId="catalog-stat-approved"
        />
        <Stat
          label="Rejected rows"
          value={state.rejectedRows.toLocaleString()}
          sub={`${state.rowsTotal === 0 ? 0 : Math.round((state.rejectedRows / state.rowsTotal) * 100)}% of total`}
          testId="catalog-stat-rejected"
        />
        <Stat
          label="Warnings"
          value={state.warnings.length}
          sub="Items flagged for review"
          testId="catalog-stat-warnings"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="px-5 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900">
              Department mappings
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Source POS codes mapped to ShelfLife departments
            </p>
          </div>
          <table className="w-full text-sm" data-testid="department-mappings-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-5 py-2.5">Source</th>
                <th className="text-left px-5 py-2.5">Target</th>
                <th className="text-right px-5 py-2.5">Items</th>
                <th className="text-left px-5 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {state.departmentMappings.map((m) => (
                <tr key={`${m.source}-${m.target}`} className="hover:bg-slate-50">
                  <td className="px-5 py-2 font-mono text-xs text-slate-700">{m.source}</td>
                  <td className="px-5 py-2 text-slate-900">{m.target}</td>
                  <td className="px-5 py-2 text-right tabular-nums">{m.items.toLocaleString()}</td>
                  <td className="px-5 py-2"><StatusPill status={m.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="px-5 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900">Import warnings</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Rows that require operator attention before activation
            </p>
          </div>
          <ul className="divide-y divide-slate-100" data-testid="import-warnings-list">
            {state.warnings.length === 0 && (
              <li className="px-5 py-6 text-sm text-slate-500 text-center">
                No warnings.
              </li>
            )}
            {state.warnings.map((w) => (
              <li key={w.code} className="px-5 py-3 flex items-start gap-3">
                <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm text-slate-900">{w.message}</div>
                  <div className="text-xs text-slate-500 mt-0.5 font-mono">
                    {w.code}
                  </div>
                </div>
                <span className="text-xs font-semibold text-slate-700 bg-slate-100 px-2 py-0.5 rounded">
                  {w.count}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-5">
          <div className="flex items-center gap-2 text-emerald-800 font-semibold">
            <CheckCircle2 className="h-5 w-5" /> Approved rows
          </div>
          <div className="mt-2 text-3xl font-semibold text-emerald-900 tabular-nums">
            {state.approvedRows.toLocaleString()}
          </div>
          <p className="text-xs text-emerald-800/80 mt-1">
            Ready to publish into the client's live catalog.
          </p>
        </div>
        <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-5">
          <div className="flex items-center gap-2 text-rose-800 font-semibold">
            <XCircle className="h-5 w-5" /> Rejected rows
          </div>
          <div className="mt-2 text-3xl font-semibold text-rose-900 tabular-nums">
            {state.rejectedRows.toLocaleString()}
          </div>
          <p className="text-xs text-rose-800/80 mt-1">
            Excluded due to missing or invalid required fields.
          </p>
        </div>
      </div>
    </div>
  );
};

const MockUploadButton = ({ icon, label, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={(e) => e.preventDefault()}
    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition-colors"
  >
    {icon} {label}
  </button>
);
